import type { TiptapCollabProvider } from '@tiptap-pro/provider';

export type CollaborationPresenceUser = {
    clientId: number;
    name: string;
    color: string;
    initials: string;
};

export type CollaborationActiveNodeState = {
    nodeId: string;
    nodeType: string;
};

type AwarenessUserFields = {
    name?: string | null;
    color?: string | null;
    initials?: string | null;
};

function parsePresenceUser(clientId: number, state: Record<string, unknown> | undefined): CollaborationPresenceUser | null {
    const user = state?.user as AwarenessUserFields | undefined;
    const name = user?.name?.trim();
    if (!name) {
        return null;
    }

    return {
        clientId,
        name,
        color: user?.color ?? '#FFFFFF',
        initials: user?.initials ?? 'AA',
    };
}

export function readCollaborationPresenceUsers(
    provider: TiptapCollabProvider,
    includeState?: (state: Record<string, unknown>) => boolean,
): CollaborationPresenceUser[] {
    const localClientId = provider?.awareness?.clientID;
    const byName = new Map<string, CollaborationPresenceUser>();

    for (const [clientId, state] of provider?.awareness?.getStates() ?? []) {
        if (clientId === localClientId) {
            continue;
        }

        const awarenessState = state as Record<string, unknown> | undefined;
        if (includeState && !includeState(awarenessState ?? {})) {
            continue;
        }

        const entry = parsePresenceUser(clientId, awarenessState);
        if (!entry || byName.has(entry.name)) {
            continue;
        }

        byName.set(entry.name, entry);
    }

    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function readDocumentPresenceUsers(provider: TiptapCollabProvider): CollaborationPresenceUser[] {
    return readCollaborationPresenceUsers(provider);
}

export function readNodePresenceUsers(provider: TiptapCollabProvider, nodeId: string): CollaborationPresenceUser[] {
    return readCollaborationPresenceUsers(provider, state => {
        const activeNode = state.activeNode as CollaborationActiveNodeState | undefined;
        return activeNode?.nodeId === nodeId;
    });
}
